import { Component, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { MatDialog } from "@angular/material/dialog";
import { ReferenceTableDialogComponent } from "./reference-table-dialog/reference-table-dialog.component";
import { AuthenticationService } from "../authentication/authentication/authentication.service";
import columns from "../../services/columns.json";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import { FormControl } from "@angular/forms";
import { ArchitectureDTO } from "src/app/model/ArchitectureDTO";
import { APLStatusDTO } from "src/app/model/APLStatusDTO";
import { ArchitectureTypeDTO } from "src/app/model/ArchitectureTypeDTO";
import { BodyStyle } from "../new-bodycycle/new-bodycycle.component";
import { BodystyleDTO } from "src/app/model/BodystyleDTO";
import { BrandDTO } from "src/app/model/BrandDTO";
import { DriveConfigDTO } from "src/app/model/DriveConfigDTO";
import { EngineeringClassDTO } from "src/app/model/EngineeringClassDTO";
import { GlobalSegmentDTO } from "src/app/model/GlobalSegmentDTO";
import { OEMDTO } from "src/app/model/OEMDTO";
import { PropulsionTypeDTO } from "src/app/model/PropulsionTypeDTO";
import { RegionDTO } from "src/app/model/RegionDTO";
import { LaunchProbabilityDTO } from "src/app/model/LaunchProbability";
import { BodystyleValueDTO } from "src/app/model/BodystyleValueDTO";

interface Route {
  columns: { [column: string]: string };
  keys: string[];
}
interface Routes {
  [table: string]: Route;
}

@Component({
    selector: "app-reference-table",
    templateUrl: "./reference-table.component.html",
    styleUrls: ["./reference-table.component.scss"],
    standalone: false
})
export class ReferenceTableComponent implements OnInit {
  table = null;
  sub = null;
  columnsToDisplay: any[] = [];
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  filterControl: FormControl = new FormControl("");

  routes = columns as Routes;

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private auth: AuthenticationService,
    private http: HttpClient
  ) {}

  public static getUrl(table): URL {
    const endpoint: string = this.getEndpoint(table);
    return new URL(`/crud/${endpoint}`, environment.serviceURL);
  }

  private static getEndpoint(table: string): string {
    switch (table) {
      case "architecture":
        return "getArchitectures";
      case "architecturetype":
        return "getArchitectureTypes";
      case "brand":
        return "getBrands";
      case "driveconfig":
        return "getDriveConfigs";
      case "engineeringclass":
        return "getEngineeringClasses";
      case "globalsegment":
        return "getGlobalSegments";
      case "launchprobability":
        return "getLaunchProbabilities";
      case "oem":
        return "getOems";
      case "status":
        return "getAPLStatuses";
      case "vehicletype":
        return "getVehicleTypes";
      case "region":
        return "getRegions";
      case "bodystylevalue":
        return "getBodystyleValues";
      case "propulsiontype":
        return "getPropulsionTypes";
      default:
        return table;
    }
  }

  public getTitle(): string {
    switch (this.table) {
      case "architecture":
        return "Architecture";
      case "architecturetype":
        return "Architecture Type";
      case "brand":
        return "Brand";
      case "driveconfig":
        return "Drive Configuration";
      case "engineeringclass":
        return "Engineering Class / Life Cycle";
      case "globalsegment":
        return "Global segment";
      case "launchprobability":
        return "Vehicle Launch Probability";
      case "oem":
        return "OEM";
      case "status":
        return "APL Status";
      case "vehicletype":
        return "Vehicle Type";
      case "region":
        return "Region";
      case "bodystylevalue":
        return "Bodystyle";
      case "propulsiontype":
        return "Propulsion Type";
      default:
        return this.table;
    }
  }

  ngOnInit(): void {
    this.sub = this.route.params.subscribe((params) => {
      this.table = params.table;
      this.columnsToDisplay = [...this.getColumns(), "saveButton"];
      if (this.isAdmin()) {
        this.columnsToDisplay.push("disableButton");
      }
      this.updateDataSource();
      this.filterControl.setValue("");
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  public applyFilter(searchTerm: string): void {
    //Filter dataSource
    this.filteredDataSource = this.dataSource.filter((row) =>
      row.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  public async updateDataSource(): Promise<void> {
    try {
      this.dataSource = await this.getData();
    } catch (e) {
      alert("updating data source failed: " + e.message);
    }

    this.filteredDataSource = [...this.dataSource];
    this.applyFilter(this.filterControl.value);
  }

  public getRoutesJson(): string {
    return JSON.stringify(this.getTableInfo(), null, "\t");
  }

  public getColumns(): string[] {
    return Object.keys(this.getTableInfo().columns).sort();
  }

  public getTableInfo(): Route {
    return this.routes[this.table];
  }

  public isAdmin(): boolean {
    return this.auth.gmUser.admin;
  }

  public async request(url: URL, method: string, body: null): Promise<any> {
    const response: Response = await fetch(url, {
      method,
      headers: {
        "content-type": "application/json",
        Authorization: this.auth.getAuthorizationHeaderValue(),
      },
      body: body && JSON.stringify(body),
    });
    return await response.json();
  }

  public async getData(): Promise<any> {
    const url: URL = ReferenceTableComponent.getUrl(this.table);
    return this.request(url, "GET", null);
  }

  public openDialog(element): void {
    const dialogRef = this.dialog.open(ReferenceTableDialogComponent, {
      data: {
        table: this.table,
        tableInfo: this.getTableInfo(),
        value: element,
        dataSource: this.dataSource,
        filteredDataSource: this.filteredDataSource,
      },
    });
  }

  public getState(element: any): string {
    return element.enabled ? "Disable" : "Enable";
  }

  public getColor(element: any): string {
    const res = element.enabled
      ? "p-button-raised p-button-primary"
      : "p-button-raised p-button-text p-button-secondary";
    return res;
  }

  public toggleElement(element: any): void {
    this.dispatchRequest(element).subscribe({
      next: () => {
        this.updateDataSource().then();
      },
      error: () => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message:
              "There was a problem attempting to " +
              (element.enabled ? "disable" : "enable") +
              " " +
              element.name +
              ". ",
          },
        });
      },
    });
  }

  public dispatchRequest(element: any): Observable<any> {
    switch (this.table) {
      case "status":
        const statusDTO: APLStatusDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<APLStatusDTO>(
          environment.serviceURL + "/crud/toggleAPLStatus",
          statusDTO
        );
      case "architecture":
        const architectureDTO: ArchitectureDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<ArchitectureDTO>(
          environment.serviceURL + "/crud/toggleArchitecture",
          architectureDTO
        );
      case "architecturetype":
        const architectureTypeDTO: ArchitectureTypeDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<ArchitectureTypeDTO>(
          environment.serviceURL + "/crud/toggleArchitectureType",
          architectureTypeDTO
        );
      case "bodystylevalue":
        const bodystyleValueDTO: BodystyleValueDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<BodystyleValueDTO>(
          environment.serviceURL + "/crud/toggleBodystyleValue",
          bodystyleValueDTO
        );
      case "brand":
        const brandDTO: BrandDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<BrandDTO>(
          environment.serviceURL + "/crud/toggleBrand",
          brandDTO
        );
      case "driveconfig":
        const driveConfigDTO: DriveConfigDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<DriveConfigDTO>(
          environment.serviceURL + "/crud/toggleDriveConfig",
          driveConfigDTO
        );
      case "engineeringclass":
        const engineeringclassDTO: EngineeringClassDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<EngineeringClassDTO>(
          environment.serviceURL + "/crud/toggleEngineeringClass",
          engineeringclassDTO
        );
      case "globalsegment":
        const globalSegmentDTO: GlobalSegmentDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<GlobalSegmentDTO>(
          environment.serviceURL + "/crud/toggleGlobalSegment",
          globalSegmentDTO
        );
      case "oem":
        const oemDTO: OEMDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<OEMDTO>(
          environment.serviceURL + "/crud/toggleOEM",
          oemDTO
        );
      case "propulsiontype":
        const propulsionTypeDTO: PropulsionTypeDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<PropulsionTypeDTO>(
          environment.serviceURL + "/crud/togglePropulsionType",
          propulsionTypeDTO
        );
      case "region":
        const regionDTO: RegionDTO = {
          name: element.name,
          acronym: element.acronym,
          enabled: !element.enabled,
        };
        return this.http.post<RegionDTO>(
          environment.serviceURL + "/crud/toggleRegion",
          regionDTO
        );
      case "launchprobability":
        const launchProbabilityDTO: LaunchProbabilityDTO = {
          name: element.name,
          enabled: !element.enabled,
        };
        return this.http.post<LaunchProbabilityDTO>(
          environment.serviceURL + "/crud/toggleLaunchProbability",
          launchProbabilityDTO
        );
    }
    return;
  }

  public add(): void {
    this.openDialog(null);
  }
}
